import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  injectBxIcons,
  barChart05Icon,
  clockIcon,
  dataflow03Icon,
  flashIcon,
  settings02Icon,
  signal01Icon,
  thermometer01Icon,
  thermometer02Icon,
  valve01Icon,
  wifiIcon,
} from '@beyonnex/bx-angular-ui';
import { formatDateTime } from '../../../models/core.models';
import { DdmMeasurementUnit } from '../../../models/unit.models';
import { CommonTranslateService } from '../../../services/common-translate/common-translate.service';
import { CurrentStatePanelComponent } from '../current-state-panel/current-state-panel.component';
import { CurrentStateDataPoint } from '../current-state-panel/current-state-panel.models';
import { DeviceDetailsStore } from '../device-details.store';

@Component({
  selector: 'bxi-srt-current-state',
  imports: [CurrentStatePanelComponent],
  templateUrl: './srt-current-state.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrtCurrentStateComponent {
  private readonly store = inject(DeviceDetailsStore);
  protected readonly commonTranslate = inject(CommonTranslateService);

  protected readonly srtDevice = toSignal(this.store.srtDevice$);
  protected readonly gatewayLink = toSignal(this.store.gatewayLink$);
  protected readonly data = computed<CurrentStateDataPoint[]>(() => {
    const srt = this.srtDevice();
    return [
      {
        label: this.commonTranslate.translateThingProperty('features.operationalMode.properties.configuration.mode'),
        value: this.commonTranslate.translateOperationalMode(srt?.features?.operationalMode?.properties?.configuration?.mode),
        icon: 'settings-02',
        iconStyle: 'text-utility-gray-blue-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.sensorTempHum.properties.status.temperature'),
        value: `${this.commonTranslate.formatUnit(srt?.features?.sensorTempHum?.properties?.status?.temperature?.unit as DdmMeasurementUnit, srt?.features?.sensorTempHum?.properties?.status?.temperature?.value)}`,
        icon: 'thermometer-01',
        iconStyle: 'text-utility-brand-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.temperatureControl.properties.configuration.targetTemperature'),
        value: `${this.commonTranslate.formatUnit(srt?.features?.temperatureControl?.properties?.configuration?.targetTemperature?.unit as DdmMeasurementUnit, srt?.features?.temperatureControl?.properties?.configuration?.targetTemperature?.value)}`,
        icon: 'thermometer-02',
        iconStyle: 'text-utility-warning-500',
        valueStyle: srt?.isTargetTemperatureEffective() ? '' : 'line-through',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.thermostat.properties.status.motorPosition'),
        value: `${this.commonTranslate.formatUnit(srt?.features?.thermostat?.properties?.status?.motorPosition?.unit as DdmMeasurementUnit, srt?.features?.thermostat?.properties?.status?.motorPosition?.value)}`,
        icon: 'valve-01',
        iconStyle: 'text-utility-brand-800-alt',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.battery.properties.status.lastIdleVoltage'),
        value: `${this.commonTranslate.formatUnit(srt?.features?.battery?.properties?.status?.lastIdleVoltage?.unit as DdmMeasurementUnit, srt?.features?.battery?.properties?.status?.lastIdleVoltage?.value)}`,
        icon: 'flash',
        iconStyle: 'text-fg-warning-primary',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.rssi'),
        value: srt?.features?.grid?.properties?.rssi,
        icon: 'bar-chart-05',
        iconStyle: 'text-teal-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.snr'),
        value: srt?.features?.grid?.properties?.snr,
        icon: 'wifi',
        iconStyle: 'text-utility-purple-500',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.dataRate'),
        value: srt?.features?.grid?.properties?.dataRate,
        icon: 'dataflow-03',
        iconStyle: 'text-utility-blue-light-600',
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.gateway'),
        value: srt?.features?.grid?.properties?.gateway,
        icon: 'signal-01',
        iconStyle: 'text-utility-gray-blue-500',
        ...this.gatewayLink(),
      },
      {
        label: this.commonTranslate.translateThingProperty('features.grid.properties.receiveTime'),
        value: formatDateTime(srt?.features?.grid?.properties?.receiveTime),
        icon: 'clock',
        iconStyle: 'text-utility-blue-light-600',
      },
    ];
  });

  constructor() {
    injectBxIcons([
      settings02Icon,
      thermometer01Icon,
      thermometer02Icon,
      signal01Icon,
      flashIcon,
      barChart05Icon,
      wifiIcon,
      clockIcon,
      valve01Icon,
      dataflow03Icon,
    ]);
  }
}
